import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Tạo axios instance với interceptor để xử lý lỗi 403
const axiosInstance = axios.create();

const clearAdminSession = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_level');
  localStorage.removeItem('admin_user_id');
  localStorage.removeItem('user_id');
  localStorage.removeItem('admin_is_super_admin');
  localStorage.removeItem('admin_hierarchy_level');
  localStorage.removeItem('admin_role_code');
  localStorage.removeItem('admin_permission_names');
  localStorage.removeItem('admin_feature_keys');
  localStorage.removeItem('admin_has_full_access');
  localStorage.removeItem('admin_is_default');
};

// Response interceptor để xử lý lỗi admin authorization
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      // User không có quyền admin - chỉ xóa session admin và redirect
      console.warn('User không có quyền admin - xóa admin session và redirect về login');
      clearAdminSession();
      
      // Redirect về login sau 1 giây
      setTimeout(() => {
        window.location.href = '/admin/login';
      }, 1000);
    }

    if (error.response?.status === 401) {
      // Token admin hết hạn hoặc không hợp lệ
      console.warn('Admin token hết hạn hoặc không hợp lệ - redirect về admin login');
      clearAdminSession();
      setTimeout(() => {
        window.location.href = '/admin/login';
      }, 1000);
    }

    return Promise.reject(error);
  }
);

/** Node từ GET /admin/hierarchy/tree (trùng tab Phân cấp). */
export interface HierarchyTreeNode {
  id: number;
  admin_id: number;
  username: string;
  full_name: string;
  level: number;
  parent_admin_id?: number | null;
  is_active?: boolean;
  children?: HierarchyTreeNode[];
}

type QueryParams = Record<string, string | number | boolean | undefined>;
type BlacklistRestrictions = Record<string, unknown>;

// Admin API service
export const adminApi = {
  // Get admin token - CHỈ LẤY admin_token, KHÔNG BAO GIỜ DÙNG token của user thường
  getAuthHeaders: () => {
    // CHỈ CHECK admin_token - TÁCH BIỆT HOÀN TOÀN VỚI USER THƯỜNG
    const adminToken = localStorage.getItem('admin_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    // Only add Authorization header if admin token exists
    if (adminToken) {
      headers['Authorization'] = `Bearer ${adminToken}`;
    } else {
      console.warn('No admin token found in localStorage. Please login as admin.');
      clearAdminSession();
    }
    
    return headers;
  },

  getMe: async () => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/me`, {
      headers: adminApi.getAuthHeaders(),
    });
    return response.data;
  },

  getPermissionsOverview: async () => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/permissions/overview`, {
      headers: adminApi.getAuthHeaders(),
    });
    return response.data;
  },

  updateAdminUserFeatures: async (adminUserId: number, grantedKeys: string[]) => {
    const response = await axiosInstance.put(
      `${API_BASE_URL}/admin/users/${adminUserId}/features`,
      { granted_keys: grantedKeys },
      { headers: adminApi.getAuthHeaders() }
    );
    return response.data;
  },

  syncAdminSessionFromMe: async () => {
    const me = await adminApi.getMe();
    if (me.admin_level !== undefined) {
      localStorage.setItem('admin_level', String(me.admin_level));
    }
    if (me.hierarchy_level !== undefined) {
      localStorage.setItem('admin_hierarchy_level', String(me.hierarchy_level));
    }
    if (me.role_code) {
      localStorage.setItem('admin_role_code', me.role_code);
    }
    localStorage.setItem('admin_is_super_admin', me.is_super_admin ? 'true' : 'false');
    if (me.permission_names) {
      localStorage.setItem('admin_permission_names', JSON.stringify(me.permission_names));
    }
    if (me.feature_keys) {
      localStorage.setItem('admin_feature_keys', JSON.stringify(me.feature_keys));
    }
    if (me.has_full_access !== undefined) {
      localStorage.setItem('admin_has_full_access', me.has_full_access ? 'true' : 'false');
    }
    if (me.is_default !== undefined) {
      localStorage.setItem('admin_is_default', me.is_default ? 'true' : 'false');
    }
    if (me.admin_id) {
      localStorage.setItem('admin_user_id', String(me.admin_id));
    }
    return me;
  },

  // Admin Roles
  getRoles: async (skip = 0, limit = 100) => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/roles`, {
      params: { skip, limit },
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  createRole: async (roleData: { name: string; description?: string }) => {
    const response = await axiosInstance.post(`${API_BASE_URL}/admin/roles`, roleData, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  getRolePermissions: async (roleId: number) => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/roles/${roleId}/permissions`, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  createRolePermission: async (roleId: number, permissionData: { permission_name: string; resource?: string; action?: string; is_granted?: boolean }) => {
    const response = await axiosInstance.post(`${API_BASE_URL}/admin/roles/${roleId}/permissions`, permissionData, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  // Admin Users
  getAdminUsers: async (skip = 0, limit = 100, roleId?: number, isActive?: boolean, search?: string) => {
    const params: QueryParams = { skip, limit };
    if (roleId) params.role_id = roleId;
    if (isActive !== undefined) params.is_active = isActive;
    if (search) params.search = search;
    
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/users`, {
      params,
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  createAdminUser: async (adminData: { user_id: number; role_id: number }) => {
    const response = await axiosInstance.post(`${API_BASE_URL}/admin/users`, adminData, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  // Admin Hierarchy
  getHierarchy: async () => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/hierarchy`, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  /** Cây phân cấp — cùng nguồn hiển thị tab Phân cấp (level theo từng admin_id). */
  getHierarchyTree: async (): Promise<{ tree: HierarchyTreeNode[]; total_nodes?: number }> => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/hierarchy/tree`, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  createHierarchy: async (adminUserId: number, parentAdminId?: number) => {
    const response = await axiosInstance.post(`${API_BASE_URL}/admin/hierarchy`, null, {
      params: { admin_user_id: adminUserId, parent_admin_id: parentAdminId },
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  // Security Alerts
  getSecurityAlerts: async (skip = 0, limit = 100, severity?: string, isResolved?: boolean) => {
    const params: QueryParams = { skip, limit };
    if (severity) params.severity = severity;
    if (isResolved !== undefined) params.is_resolved = isResolved;
    
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/security-alerts`, {
      params,
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  // System Settings
  getSystemSettings: async (category?: string, isPublic?: boolean) => {
    const params: QueryParams = {};
    if (category) params.category = category;
    if (isPublic !== undefined) params.is_public = isPublic;
    
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/settings`, {
      params,
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  getSystemSetting: async (settingKey: string) => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/settings/${settingKey}`, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  // User Management
  getUsersList: async (
    skip = 0,
    limit = 100,
    search?: string,
    isActive?: boolean,
    isVerified?: boolean,
    hasLandlordAccount?: boolean,
  ) => {
    const params: QueryParams = { skip, limit };
    if (search) params.search = search;
    if (isActive !== undefined) params.is_active = isActive;
    if (isVerified !== undefined) params.is_verified = isVerified;
    if (hasLandlordAccount !== undefined) params.has_landlord_account = hasLandlordAccount;
    
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/users/list`, {
      params,
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  getUserDetail: async (userId: number) => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/users/${userId}`, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  deleteUserSePayAccount: async (userId: number) => {
    const response = await axiosInstance.delete(
      `${API_BASE_URL}/admin/users/${userId}/sepay-account`,
      { headers: adminApi.getAuthHeaders() },
    );
    return response.data;
  },

  updateUserIdentity: async (
    userId: number,
    data: {
      full_name?: string;
      email?: string;
      phone?: string;
      address?: string;
      cccd?: string;
      is_verified?: boolean;
    },
  ) => {
    const response = await axiosInstance.put(
      `${API_BASE_URL}/admin/users/${userId}/identity`,
      data,
      { headers: adminApi.getAuthHeaders() },
    );
    return response.data;
  },

  blacklistUser: async (userId: number, reason: string, blacklistType = 'full', expiresAt?: string, restrictions?: BlacklistRestrictions) => {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/admin/users/${userId}/blacklist`,
      { reason, blacklist_type: blacklistType, expires_at: expiresAt, restrictions },
      { headers: adminApi.getAuthHeaders() }
    );
    return response.data;
  },

  unblacklistUser: async (userId: number) => {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/admin/users/${userId}/unblacklist`,
      {},
      { headers: adminApi.getAuthHeaders() }
    );
    return response.data;
  },

  unlockUserAccount: async (userId: number) => {
    const response = await axiosInstance.put(
      `${API_BASE_URL}/admin/users/${userId}/unlock`,
      {},
      { headers: adminApi.getAuthHeaders() }
    );
    return response.data;
  },

  deleteUserAccount: async (userId: number) => {
    const response = await axiosInstance.delete(
      `${API_BASE_URL}/admin/users/${userId}`,
      { headers: adminApi.getAuthHeaders() }
    );
    return response.data;
  },

  // Blacklist Management
  getBlacklistedUsers: async (skip = 0, limit = 100) => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/users/blacklist/list`, {
      params: { skip, limit },
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },

  // Statistics
  getUsersStatistics: async () => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/users/statistics`, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data;
  },



  getAllUserSubscriptions: async (status = 'active', skip = 0, limit = 100) => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/users/subscriptions/all`, {
      params: { status, offset: skip, limit },
      headers: adminApi.getAuthHeaders()
    });
    return response.data.data || [];
  },



  getSubscriptionPlans: async () => {
    const response = await axiosInstance.get(`${API_BASE_URL}/admin/users/subscriptions/plans`, {
      headers: adminApi.getAuthHeaders()
    });
    return response.data || [];
  },

  manualUpgradeUserSubscription: async (userId: number, planId: string, durationMonths = 1) => {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/admin/users/subscriptions/manual-upgrade`,
      { user_id: userId, plan_id: planId, duration_months: durationMonths },
      { headers: adminApi.getAuthHeaders() }
    );
    return response.data;
  },

  createOnRequestPayment: async (userId: number, durationMonths: number, roomCount: number) => {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/admin/users/subscriptions/on-request-payment`,
      { user_id: userId, duration_months: durationMonths, room_count: roomCount },
      { headers: adminApi.getAuthHeaders() }
    );
    return response.data;
  },

  confirmOnRequestPayment: async (orderCode: string) => {
    const response = await axiosInstance.post(
      `${API_BASE_URL}/admin/users/subscriptions/on-request-confirm-payment`,
      { order_code: orderCode },
      { headers: adminApi.getAuthHeaders() }
    );
    return response.data;
  },
};

// Types
export interface AdminRole {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminPermission {
  id: number;
  role_id: number;
  permission_name: string;
  resource?: string;
  action?: string;
  is_granted: boolean;
  created_at: string;
}

export interface AdminUser {
  id: number;
  user_id: number;
  role_id: number;
  is_active: boolean;
  is_default?: boolean;
  is_protected?: boolean;
  /** Cấp phân cấp (1–4), từ GET /admin/users */
  hierarchy_level?: number | null;
  last_login?: string;
  login_count: number;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    email: string;
    username: string;
    full_name?: string;
  };
  role?: {
    id: number;
    name: string;
    description?: string;
  };
}

export interface AdminHierarchy {
  id: number;
  admin_user_id: number;
  parent_admin_id?: number;
  level: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SecurityAlert {
  id: number;
  alert_type: string;
  severity: string;
  title: string;
  message: string;
  user_id?: number;
  is_resolved: boolean;
  resolved_at?: string;
  created_at: string;
}

export interface SystemSetting {
  id: number;
  setting_key: string;
  setting_value?: string;
  setting_type: string;
  category?: string;
  description?: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

